import { dbManager } from './db_manager';

export const notificationService = {
    async getNotifications(userId: string) {
        const result = await dbManager.execute({
            action: 'select_public',
            table: 'notifications',
            filters: { user_id: userId },
            join: 'notifications_profiles'
        });
        
        let notifications = Array.isArray(result) ? result : (result?.data || []);
        
        // Sort descending by created_at since D1 might not order it natively through this join if not specified
        notifications.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        
        return notifications.map((n: any) => ({
            ...n,
            actor: n.actor_id ? {
                id: n.actor_id,
                username: n.profiles?.username || 'Usuário',
                display_name: n.profiles?.display_name || n.profiles?.username || 'Usuário',
                avatar_url: n.profiles?.avatar_url,
                avatar_config: n.profiles?.avatar_config,
                plan_type: n.profiles?.plan_type
            } : null
        }));
    },

    async getUnreadCount(userId: string) {
        const notifications = await dbManager.from('notifications', userId).select({ user_id: userId, is_read: 0 });
        return notifications?.length || 0;
    },

    async markAsRead(notificationId: string, userId: string) {
        return dbManager.execute({
            action: 'update_filtered',
            table: 'notifications',
            data: { is_read: 1 },
            filters: { id: notificationId },
            user_id: userId
        });
    },

    async markAllAsRead(userId: string) {
        // As a workaround for D1 atomic updates, we fetch unread and update them
        const unread = await dbManager.from('notifications', userId).select({ user_id: userId, is_read: 0 });
        if (!unread || unread.length === 0) return;
        
        await Promise.all(unread.map((n: any) => 
            this.markAsRead(n.id, userId)
        ));
    },

    async createNotification(userId: string, actorId: string, type: 'mention' | 'like' | 'comment' | 'reply' | 'streak', targetType: 'post' | 'comment' | 'club_comment' | 'streak_milestone', targetId: string) {
        if (userId === actorId) return; // Don't notify yourself

        const id = `notif_${Date.now()}_${Math.random().toString(36).substring(7)}`;
        return dbManager.execute({
            action: 'insert',
            table: 'notifications',
            data: {
                id,
                user_id: userId,
                actor_id: actorId,
                type,
                target_type: targetType,
                target_id: targetId,
                is_read: 0,
                created_at: new Date().toISOString()
            }
        });
    },

    async notifyFriendsStreakMilestone(userId: string, streak: number) {
        // Milestone check: 1, 2, 3, 5, then multiples of 5 (10, 15, 20, 25...)
        const isMilestone = streak === 1 || streak === 2 || streak === 3 || (streak >= 5 && streak % 5 === 0);
        if (!isMilestone) return;

        try {
            const allFriendships = await dbManager.from('friendships', userId).select(
                { OR_USER_FRIEND: userId },
                undefined,
                'friendships'
            );
            
            if (!allFriendships || !Array.isArray(allFriendships)) return;

            const acceptedFriendships = allFriendships.filter((f: any) => f.status === 'accepted');
            if (acceptedFriendships.length === 0) return;

            for (const f of acceptedFriendships) {
                const friendId = f.user_id === userId ? f.friend_id : f.user_id;
                if (!friendId || friendId === userId) continue;

                const id = `notif_streak_${streak}_${friendId}_${Date.now()}`;
                dbManager.execute({
                    action: 'insert',
                    table: 'notifications',
                    data: {
                        id,
                        user_id: friendId,
                        actor_id: userId,
                        type: 'streak',
                        target_type: 'streak_milestone',
                        target_id: String(streak),
                        is_read: 0,
                        created_at: new Date().toISOString()
                    }
                }).catch(err => console.warn('[NotificationService] Failed to notify friend of streak:', err));
            }
        } catch (err) {
            console.error('[NotificationService] Failed to notify friends of streak milestone:', err);
        }
    }
};
